
import React, { useEffect, useState } from "react";
import "./Inventory.css";

const Inventory = () => {
    const [inventory, setInventory] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);

    // OTP modal states
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [otpAction, setOtpAction] = useState("");
    const [selectedInventoryId, setSelectedInventoryId] = useState(null);
    const [selectedDownloadVendor, setSelectedDownloadVendor] = useState("");
    const [otpRecipientEmail, setOtpRecipientEmail] = useState("");

    // Vendor selection modal
    const [showVendorModal, setShowVendorModal] = useState(false);

    // Loading states
    const [sendingOtp, setSendingOtp] = useState(false);
    const [processingOtp, setProcessingOtp] = useState(false);

    // ============================================================
    // FETCH INVENTORY
    // ============================================================

    const fetchInventory = async () => {
        try {
            setLoading(true);

            const response = await fetch("/inventory");

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            setInventory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching inventory:", error);
            setInventory([]);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // FETCH VENDORS
    // ============================================================

    const fetchVendors = async () => {
        try {
            const response = await fetch("/vendors");

            if (!response.ok) {
                throw new Error("Failed to load vendors.");
            }

            const data = await response.json();

            setVendors(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching vendors:", error);
            setVendors([]);
        }
    };

    // ============================================================
    // INITIAL LOAD
    // ============================================================

    useEffect(() => {
        fetchInventory();
        fetchVendors();
    }, []);

    // ============================================================
    // RESET OTP MODAL
    // ============================================================

    const resetOtpModal = () => {
        setShowOtpModal(false);
        setOtpCode("");
        setOtpAction("");
        setSelectedInventoryId(null);
        setSelectedDownloadVendor("");
        setOtpRecipientEmail("");
    };

    // ============================================================
    // OPEN INVENTORY DOWNLOAD VENDOR SELECTION
    // ============================================================

    const downloadInventory = () => {
        if (vendors.length === 0) {
            alert(
                "No vendors found. Please add a vendor before downloading."
            );
            return;
        }

        setSelectedDownloadVendor("");
        setShowVendorModal(true);
    };

    // ============================================================
    // SEND OTP FOR INVENTORY EXCEL DOWNLOAD
    // ============================================================

    const sendInventoryDownloadOtp = async () => {
        const vendorId = String(selectedDownloadVendor || "").trim();

        if (!vendorId) {
            alert("Please select a vendor.");
            return;
        }

        const vendor = vendors.find(
            (item) => String(item.vendor_id).trim() === vendorId
        );

        if (!vendor) {
            alert("Selected vendor was not found. Please refresh and try again.");
            return;
        }

        if (!vendor.email || !String(vendor.email).trim()) {
            alert(
                "This vendor does not have a registered email address. " +
                "Please update the vendor details first."
            );
            return;
        }

        try {
            setSendingOtp(true);

            const response = await fetch("/send-delete-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    vendor_id: vendorId
                })
            });

            const result = await response.json();

            if (!response.ok) {
                alert(
                    result.message ||
                    "Failed to send the verification code."
                );
                return;
            }

            setSelectedDownloadVendor(vendorId);
            setOtpRecipientEmail(
                result.email ||
                result.masked_email ||
                vendor.email
            );

            setOtpCode("");
            setOtpAction("download");
            setSelectedInventoryId(null);

            setShowVendorModal(false);
            setShowOtpModal(true);

            alert("Verification code sent to the vendor's registered email.");
        } catch (error) {
            console.error("Inventory download OTP error:", error);

            alert("Unable to send the verification code. Please try again.");
        } finally {
            setSendingOtp(false);
        }
    };

    // ============================================================
    // DELETE INVENTORY - SEND OTP TO THE ASSOCIATED VENDOR
    // ============================================================

    const deleteInventory = async (row) => {
        const inventoryId = row.inventory_id;
        const vendorId = String(row.vendor_id || "").trim();

        if (!inventoryId || !vendorId) {
            alert(
                "Inventory or vendor information is missing. Please refresh the page."
            );
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete inventory record ${inventoryId}? ` +
            `A verification code will be sent to the registered email of ` +
            `${row.vendor_name || "the associated vendor"}.`
        );

        if (!confirmed) {
            return;
        }

        const vendor = vendors.find(
            (item) => String(item.vendor_id).trim() === vendorId
        );

        if (vendor && !vendor.email) {
            alert(
                "This vendor does not have a registered email address. " +
                "Please update the vendor details first."
            );
            return;
        }

        try {
            setSendingOtp(true);

            const response = await fetch("/send-delete-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    vendor_id: vendorId
                })
            });

            const result = await response.json();

            if (!response.ok) {
                alert(
                    result.message ||
                    "Failed to send the verification code."
                );
                return;
            }

            setSelectedInventoryId(inventoryId);
            setSelectedDownloadVendor(vendorId);
            setOtpRecipientEmail(
                result.email ||
                result.masked_email ||
                (vendor ? vendor.email : "")
            );

            setOtpCode("");
            setOtpAction("delete");
            setShowOtpModal(true);

            alert("Verification code sent to the vendor's registered email.");
        } catch (error) {
            console.error("Inventory delete OTP error:", error);

            alert("Unable to send the verification code. Please try again.");
        } finally {
            setSendingOtp(false);
        }
    };

    // ============================================================
    // VERIFY OTP - DELETE OR DOWNLOAD
    // ============================================================

    const verifyInventoryOtp = async () => {
        const enteredCode = otpCode.trim();
        const vendorId = String(selectedDownloadVendor || "").trim();

        if (!/^\d{6}$/.test(enteredCode)) {
            alert("Please enter a valid 6-digit verification code.");
            return;
        }

        if (!vendorId) {
            alert("Vendor information is missing. Please try again.");
            return;
        }

        try {
            setProcessingOtp(true);

            // ----------------------------------------------------
            // DELETE INVENTORY
            // ----------------------------------------------------

            if (otpAction === "delete") {
                if (!selectedInventoryId) {
                    alert(
                        "Inventory information is missing. Please try again."
                    );
                    return;
                }

                const response = await fetch(
                    `/inventory/${selectedInventoryId}`,
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            vendor_id: vendorId,
                            code: enteredCode
                        })
                    }
                );

                const result = await response.json();

                if (!response.ok) {
                    alert(
                        result.message ||
                        "Invalid verification code or inventory deletion failed."
                    );
                    return;
                }

                resetOtpModal();

                alert("Inventory record deleted successfully.");

                await fetchInventory();
                return;
            }

            // ----------------------------------------------------
            // DOWNLOAD INVENTORY EXCEL
            // ----------------------------------------------------

            if (otpAction === "download") {
                const response = await fetch("/inventory/export", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        vendor_id: vendorId,
                        code: enteredCode
                    })
                });

                if (!response.ok) {
                    let result = {};

                    try {
                        result = await response.json();
                    } catch (error) {
                        // The response may not contain JSON.
                    }

                    alert(
                        result.message ||
                        "Invalid verification code or inventory download failed."
                    );
                    return;
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");

                link.href = url;
                link.download = "inventory_craftcounter.xlsx";

                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);

                resetOtpModal();

                alert("Inventory Excel downloaded successfully.");
            }
        } catch (error) {
            console.error("Inventory OTP verification error:", error);

            alert("Unable to process verification. Please try again.");
        } finally {
            setProcessingOtp(false);
        }
    };

    // ============================================================
    // OPEN PRODUCT IMAGE
    // ============================================================

    const openImage = (imagePath) => {
        if (!imagePath) {
            return;
        }

        window.open(imagePath, "_blank", "noopener,noreferrer");
    };

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="inventory-page">

            {/* HEADER */}

            <div className="inventory-header">
                <div>
                    <h1>Inventory</h1>
                    <p>View current stock and inventory balance</p>
                </div>

                <div className="inventory-header-actions">
                    <button
                        type="button"
                        className="download-inventory-btn"
                        onClick={downloadInventory}
                        disabled={sendingOtp || processingOtp}
                    >
                        Download
                    </button>
                </div>
            </div>

            {/* INVENTORY TABLE */}

            <div className="inventory-table-container">
                {loading ? (
                    <p>Loading inventory...</p>
                ) : inventory.length === 0 ? (
                    <p>
                        No inventory records found. Add products through Inwards.
                    </p>
                ) : (
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>SKU</th>
                                <th>Vendor ID</th>
                                <th>Vendor</th>
                                <th>Category</th>
                                <th>Craft Type</th>
                                <th>Design No.</th>
                                <th>Item</th>
                                <th>Image</th>
                                <th>Inwards</th>
                                <th>Outwards</th>
                                <th>Balance</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {inventory.map((row) => (
                                <tr key={row.inventory_id}>
                                    <td>{row.inventory_id}</td>

                                    <td className="sku-cell">
                                        {row.sku}
                                    </td>

                                    <td>{row.vendor_id}</td>
                                    <td>{row.vendor_name}</td>

                                    <td>
                                        {row.category_name || row.category_code}
                                    </td>

                                    <td>
                                        {row.craft_name || row.craft_code}
                                    </td>

                                    <td>{row.design_number}</td>
                                    <td>{row.item_name}</td>

                                    <td className="image-cell">
                                        {row.image_path ? (
                                            <img
                                                src={row.image_path}
                                                alt={row.item_name || "Product"}
                                                className="inventory-image"
                                                onClick={() =>
                                                    openImage(row.image_path)
                                                }
                                                title="Click to view image"
                                                style={{ cursor: "pointer" }}
                                            />
                                        ) : (
                                            <span className="inventory-no-image">
                                                No Image
                                            </span>
                                        )}
                                    </td>

                                    <td>{row.inwards}</td>
                                    <td>{row.outwards}</td>

                                    <td>
                                        <strong
                                            className={
                                                Number(row.balance) <= 0
                                                    ? "balance-zero"
                                                    : "balance-positive"
                                            }
                                        >
                                            {row.balance}
                                        </strong>
                                    </td>

                                    <td>
                                        <button
                                            type="button"
                                            className="delete-btn"
                                            onClick={() => deleteInventory(row)}
                                            disabled={sendingOtp || processingOtp}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ====================================================
                SELECT VENDOR MODAL - DOWNLOAD
            ==================================================== */}

            {showVendorModal && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal">
                        <h2>Select Vendor</h2>

                        <p>
                            Select the vendor whose registered email will
                            receive the verification code for downloading
                            the inventory Excel file.
                        </p>

                        <select
                            value={selectedDownloadVendor}
                            onChange={(event) =>
                                setSelectedDownloadVendor(event.target.value)
                            }
                            className="download-vendor-select"
                            disabled={sendingOtp}
                        >
                            <option value="">-- Select Vendor --</option>

                            {vendors.map((vendor) => (
                                <option
                                    key={vendor.vendor_id}
                                    value={String(vendor.vendor_id)}
                                >
                                    {vendor.vendor_name} ({vendor.vendor_id})
                                </option>
                            ))}
                        </select>

                        <div className="delete-modal-buttons">
                            <button
                                type="button"
                                className="cancel-delete-btn"
                                onClick={() => {
                                    setShowVendorModal(false);
                                    setSelectedDownloadVendor("");
                                }}
                                disabled={sendingOtp}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="verify-delete-btn"
                                onClick={sendInventoryDownloadOtp}
                                disabled={
                                    sendingOtp || !selectedDownloadVendor
                                }
                            >
                                {sendingOtp ? "Sending OTP..." : "Send OTP"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ====================================================
                OTP VERIFICATION MODAL
            ==================================================== */}

            {showOtpModal && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal">
                        <h2>Verify OTP</h2>

                        <p>
                            A 6-digit verification code has been sent to:
                        </p>

                        <strong>
                            {otpRecipientEmail || "the vendor's registered email"}
                        </strong>

                        <p className="delete-modal-info">
                            Open the recipient's email inbox, copy the
                            verification code, and enter it below.
                        </p>

                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="Enter 6-digit code"
                            value={otpCode}
                            onChange={(event) => {
                                const value = event.target.value.replace(
                                    /\D/g,
                                    ""
                                );
                                setOtpCode(value);
                            }}
                            autoFocus
                            disabled={processingOtp}
                        />

                        <div className="delete-modal-buttons">
                            <button
                                type="button"
                                className="cancel-delete-btn"
                                onClick={resetOtpModal}
                                disabled={processingOtp}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="verify-delete-btn"
                                onClick={verifyInventoryOtp}
                                disabled={
                                    processingOtp ||
                                    otpCode.length !== 6
                                }
                            >
                                {processingOtp
                                    ? otpAction === "delete"
                                        ? "Deleting..."
                                        : "Downloading..."
                                    : otpAction === "delete"
                                        ? "Verify & Delete"
                                        : "Verify & Download"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inventory;